import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Send, Undo2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label } from "@/components/common";
import { Button } from "@/components/ui/button";

function Item({ i, onToggle }) {
  const done = !!i.resubmitted_at;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 py-2 ${done ? "opacity-60" : ""}`} data-testid={`reject-item-${i.line_id}`}>
      <div className="min-w-0">
        <p className={`truncate font-semibold ${done ? "line-through" : ""}`}>{i.patient}</p>
        <p className="text-xs text-gray-400">{i.payer} · soins {fmtDate(i.act_date)}{done ? ` · renvoyée le ${fmtDate(i.resubmitted_at)}` : ""}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="tabular-nums">{fmtEUR2(i.amount)}</span>
        <Button size="sm" variant={done ? "ghost" : "outline"} onClick={() => onToggle(i)} data-testid={`reject-toggle-${i.line_id}`} className="rounded-full">
          {done ? <><Undo2 className="mr-1.5 h-4 w-4" />Annuler</> : <><Send className="mr-1.5 h-4 w-4" />Marquer renvoyée</>}
        </Button>
      </div>
    </div>
  );
}

function Group({ g, onToggle }) {
  const [open, setOpen] = useState(g.todo > 0);
  return (
    <div className="px-5 py-3.5" data-testid="reject-group">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 text-left" data-testid="reject-group-toggle">
        <span className="flex min-w-0 items-center gap-2">
          <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
          <span className="min-w-0"><span className="block font-semibold">{g.reason}</span>
            <span className="text-xs text-gray-400">{g.todo} à renvoyer · {g.count - g.todo} déjà renvoyée(s)</span></span>
        </span>
        <span className="shrink-0 font-semibold tabular-nums">{fmtEUR2(g.amount)}</span>
      </button>
      {open && <div className="mt-1 divide-y divide-gray-50 pl-6 text-sm">{g.items.map((i) => <Item key={i.line_id} i={i} onToggle={onToggle} />)}</div>}
    </div>
  );
}

export function Rejets({ refreshKey }) {
  const [groups, setGroups] = useState([]);
  const load = useCallback(() => api.get("/tiers-payant/rejects").then((r) => setGroups(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const toggle = async (i) => {
    try {
      await api.post(`/tiers-payant/statements/${i.statement_id}/lines/${i.line_id}/resubmit`);
      toast.success(i.resubmitted_at ? "Remise à renvoyer" : "Marquée comme renvoyée");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const todo = groups.reduce((s, g) => s + g.todo, 0);
  return (
    <div data-testid="tp-rejects">
      <div className="mb-2"><Label>Rejets à renvoyer aux mutuelles ({todo})</Label>
        <p className="text-xs text-gray-400">Lignes rejetées sur les relevés importés, regroupées par motif. Marquez-les une fois la facture renvoyée.</p></div>
      <div className={`${CARD} divide-y divide-gray-100`}>
        {groups.map((g) => <Group key={g.reason} g={g} onToggle={toggle} />)}
        {!groups.length && <p className="p-8 text-center text-sm text-gray-400" data-testid="rejects-empty">Aucun rejet sur les relevés importés.</p>}
      </div>
    </div>
  );
}
